import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageShell from '../components/ui/PageShell';
import Attachments from '../components/Attachments';
import Button from '../components/ui/Button';
import FormField from '../components/ui/FormField';
import { apiClient } from '../utils/apiClient';
import './EditInvoice.css';
import { confirmDialog, alertDialog } from '../utils/dialogs';

const customerLabel = (customer) => {
  if (!customer) return '';
  const name = [customer.first_name, customer.last_name].filter(Boolean).join(' ');
  return customer.companyName || name || customer.email;
};

function EditInvoice() {
  const navigate = useNavigate();
  const { invoiceNumber } = useParams();

  const [invoiceData, setInvoiceData] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [formData, setFormData] = useState({ status: '', payment_method: '', amount_paid: '' });
  const [saving, setSaving] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const previewUrlRef = useRef(null);

  const [catalogItems, setCatalogItems] = useState([]);
  const [catalogSearch, setCatalogSearch] = useState('');
  const [catalogResults, setCatalogResults] = useState([]);
  const [newItems, setNewItems] = useState([]);
  const [editedExisting, setEditedExisting] = useState({});

  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [changingCustomer, setChangingCustomer] = useState(false);

  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        const data = await apiClient.post('/api/invoices/GetInvoiceDetails', { invoice_number: invoiceNumber });
        setInvoiceData(data);
        setSelectedCustomerId(data?.user_id ? String(data.user_id) : '');
        setFormData({
          status: data?.payment_status || '',
          payment_method: data?.payment_method || '',
          amount_paid: data?.amount_paid ?? ''
        });
      } catch (error) {
        console.error('Error fetching invoice:', error);
      }
    };

    const fetchCustomers = async () => {
      try {
        setCustomers(await apiClient.post('/api/users/findall', {}));
      } catch (error) {
        console.error('Error fetching customers:', error);
      }
    };

    const fetchPurchases = async () => {
      try {
        setPurchases(await apiClient.post('/api/purchases/GetPurchases', { invoice_number: invoiceNumber }));
      } catch (error) {
        console.error('Error fetching invoice details:', error);
      }
    };

    const fetchCatalog = async () => {
      try {
        setCatalogItems(await apiClient.get('/api/PurchasableItems'));
      } catch (error) {
        console.error('Error fetching purchasable items:', error);
      }
    };

    fetchInvoice();
    fetchCustomers();
    fetchPurchases();
    fetchCatalog();
  }, [invoiceNumber]);

  const handleCatalogSearch = (e) => {
    const value = e.target.value;
    setCatalogSearch(value);
    if (!value.trim()) { setCatalogResults([]); return; }
    const search = value.toLowerCase();
    setCatalogResults(catalogItems.filter((item) =>
      item.item_name?.toLowerCase().includes(search) || item.item_description?.toLowerCase().includes(search)
    ));
  };

  const addCatalogItem = (item) => {
    setNewItems((prev) => [...prev, {
      _key: `${item.id}-${Date.now()}`,
      product_type: item.item_type,
      product_name: item.item_name,
      remarks: item.item_description || '',
      line_item_full_price: item.item_price,
      line_item_discount: 0
    }]);
    setCatalogSearch('');
    setCatalogResults([]);
  };

  const removeNewItem = (key) => setNewItems((prev) => prev.filter((item) => item._key !== key));

  const updateNewItemDiscount = (key, discount) => {
    setNewItems((prev) => prev.map((item) => item._key === key ? { ...item, line_item_discount: discount } : item));
  };

  const newItemsTotal = newItems.reduce((sum, item) => sum + (Number(item.line_item_full_price || 0) - Number(item.line_item_discount || 0)), 0);

  const getFieldValue = (purchase, field) => editedExisting[purchase.id]?.[field] ?? purchase[field] ?? '';

  const updateExistingField = (purchase, field, value) => {
    setEditedExisting((prev) => ({
      ...prev,
      [purchase.id]: {
        id: purchase.id,
        product_type: getFieldValue(purchase, 'product_type'),
        product_name: getFieldValue(purchase, 'product_name'),
        remarks: getFieldValue(purchase, 'remarks'),
        line_item_full_price: getFieldValue(purchase, 'line_item_full_price'),
        line_item_discount: getFieldValue(purchase, 'line_item_discount') || 0,
        [field]: value
      }
    }));
  };

  const refreshPreview = useCallback(async () => {
    setPreviewLoading(true);
    try {
      const response = await apiClient.post('/api/invoices/PreviewInvoice', {
        invoiceNum: invoiceNumber,
        payment_status: formData.status,
        payment_method: formData.payment_method,
        amount_paid: formData.amount_paid,
        newItems,
        editedItems: Object.values(editedExisting)
      }, { raw: true });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        if (previewUrlRef.current) window.URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = url;
        setPreviewUrl(url);
      } else {
        console.error('Failed to generate preview');
      }
    } catch (error) {
      console.error('Error generating preview:', error);
    } finally {
      setPreviewLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceNumber, formData, newItems, editedExisting]);

  // Debounced live preview - regenerates ~600ms after the last edit rather
  // than on every keystroke/selection change.
  useEffect(() => {
    if (!formData.status) return undefined;
    const timeout = setTimeout(() => refreshPreview(), 600);
    return () => clearTimeout(timeout);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, newItems, editedExisting]);

  useEffect(() => () => {
    if (previewUrlRef.current) window.URL.revokeObjectURL(previewUrlRef.current);
  }, []);

  // A separate action from the main "Update Invoice" save, not folded into
  // handleSubmit - reassigning who an invoice (and its line items, see the
  // API side) belongs to is a bigger deal than a status/payment tweak and
  // shouldn't happen as a side effect of saving something else.
  const changeCustomer = async () => {
    if (!selectedCustomerId || String(selectedCustomerId) === String(invoiceData?.user_id)) return;

    const target = customers.find((c) => String(c.id) === String(selectedCustomerId));
    const targetLabel = customerLabel(target) || 'this customer';
    if (!await confirmDialog(`Move invoice ${invoiceNumber} to ${targetLabel}? Its line items move with it, and this doesn't resend anything already emailed out.`)) {
      return;
    }

    setChangingCustomer(true);
    try {
      const updated = await apiClient.post('/api/invoices/UpdateInvoiceCustomer', {
        invoice_number: invoiceNumber,
        user_id: selectedCustomerId
      });
      setInvoiceData(updated);
      setSelectedCustomerId(updated?.user_id ? String(updated.user_id) : '');
    } catch (error) {
      console.error('Error changing invoice customer:', error);
      alertDialog(`Error: ${error.message}`);
    } finally {
      setChangingCustomer(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      try {
        await apiClient.post('/api/invoices/UpdateInvoiceStatus', {
          invoice_number: invoiceNumber,
          payment_status: formData.status,
          payment_method: formData.payment_method,
          amount_paid: parseFloat(formData.amount_paid || 0),
        });
      } catch (err) {
        console.error('Error updating invoice:', err.message);
        alertDialog(`Error: ${err.message}`);
        return;
      }

      if (newItems.length > 0) {
        try {
          await apiClient.post('/api/purchases/AddLineItems', { invoice_number: invoiceNumber, items: newItems });
        } catch (err) {
          alertDialog(`Invoice updated, but the new line items failed to save: ${err.message}. Try adding them again.`);
          return;
        }
      }

      // Sequential, not parallel - each PUT recalculates the invoice's
      // total from a fresh read of all its line items, so overlapping
      // requests could race and one could compute the total before the
      // other's write lands. One at a time keeps every recalc accurate.
      for (const edit of Object.values(editedExisting)) {
        const { id, ...fields } = edit;
        try {
          await apiClient.put(`/api/purchases/${id}`, fields);
        } catch (err) {
          alertDialog(`Invoice updated, but line item ${id} failed to save. Try editing it again.`);
          return;
        }
      }

      navigate('/invoices');
    } catch (error) {
      console.error('Error submitting form:', error);
      alertDialog('An error occurred while updating the invoice. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!invoiceData) return <PageShell><p>Loading...</p></PageShell>;

  return (
    <PageShell>
      <div className="edit-invoice-header">
        <h1>Edit Invoice {invoiceNumber}</h1>
      </div>

      <div className="edit-invoice-layout">
        <div className="edit-invoice-form-col">
          <div className="edit-invoice-card">
            <h3>Customer</h3>
            <p className="edit-invoice-current-customer">
              Currently:{' '}
              {invoiceData.customer?.id ? (
                <strong
                  className="edit-invoice-customer-link"
                  role="link"
                  tabIndex={0}
                  onClick={() => navigate(`/customer/${invoiceData.customer.id}`)}
                  onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/customer/${invoiceData.customer.id}`); }}
                >
                  {customerLabel(invoiceData.customer) || invoiceData.email || 'Unknown'}
                </strong>
              ) : (
                <strong>{customerLabel(invoiceData.customer) || invoiceData.email || 'Unknown'}</strong>
              )}
            </p>
            <FormField id="invoice-customer" label="Reassign to">
              <select id="invoice-customer" value={selectedCustomerId} onChange={(e) => setSelectedCustomerId(e.target.value)}>
                <option value="">Select a customer</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>{customerLabel(c)} ({c.email})</option>
                ))}
              </select>
            </FormField>
            <div className="edit-invoice-actions">
              <Button
                type="button"
                variant="secondary"
                onClick={changeCustomer}
                disabled={changingCustomer || !selectedCustomerId || String(selectedCustomerId) === String(invoiceData.user_id)}
              >
                {changingCustomer ? 'Changing...' : 'Change Customer'}
              </Button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="edit-invoice-card">
            <FormField id="status" label="Status">
              <select id="status" name="status" value={formData.status} onChange={handleChange} required>
                <option value="">Select Status</option>
                <option value="Draft">Draft</option>
                <option value="Pending Payment">Pending Payment</option>
                <option value="Paid">Paid</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </FormField>
            <FormField id="payment_method" label="Payment Method">
              <select id="payment_method" name="payment_method" value={formData.payment_method} onChange={handleChange} required>
                <option value="">Select Payment Method</option>
                <option value="Credit Card">Credit Card</option>
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="Cash">Cash</option>
                <option value="Other">Other</option>
              </select>
            </FormField>
            <FormField id="amount_paid" label="Amount Paid">
              <input type="number" id="amount_paid" name="amount_paid" value={formData.amount_paid} onChange={handleChange} min="0" step="0.01" required />
            </FormField>
            <div className="edit-invoice-actions">
              <Button type="button" variant="secondary" onClick={() => navigate('/invoices')}>Cancel</Button>
              <Button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Update Invoice'}</Button>
            </div>
          </form>

          <div className="edit-invoice-card">
            <h3>Line Items</h3>
            {purchases.length === 0 ? (
              <p className="edit-invoice-empty">No line items on this invoice.</p>
            ) : (
              <table className="edit-invoice-items-table">
                <thead>
                  <tr>
                    <th>Line No</th>
                    <th>Product Type</th>
                    <th>Product Name</th>
                    <th>Remarks</th>
                    <th>Full Price</th>
                    <th>Discount</th>
                    <th>Price (To Customer)</th>
                  </tr>
                </thead>
                <tbody>
                  {purchases.map((p) => {
                    const fullPrice = Number(getFieldValue(p, 'line_item_full_price')) || 0;
                    const discount = Number(getFieldValue(p, 'line_item_discount')) || 0;
                    return (
                      <tr key={p.id}>
                        <td>{p.invoice_line_number}</td>
                        <td>
                          <input type="text" value={getFieldValue(p, 'product_type')} onChange={(e) => updateExistingField(p, 'product_type', e.target.value)} />
                        </td>
                        <td>
                          <input type="text" value={getFieldValue(p, 'product_name')} onChange={(e) => updateExistingField(p, 'product_name', e.target.value)} />
                        </td>
                        <td>
                          <input type="text" value={getFieldValue(p, 'remarks')} onChange={(e) => updateExistingField(p, 'remarks', e.target.value)} />
                        </td>
                        <td>
                          <input type="number" min="0" step="0.01" value={getFieldValue(p, 'line_item_full_price')} onChange={(e) => updateExistingField(p, 'line_item_full_price', e.target.value)} />
                        </td>
                        <td>
                          <input type="number" min="0" step="0.01" value={getFieldValue(p, 'line_item_discount')} onChange={(e) => updateExistingField(p, 'line_item_discount', e.target.value)} />
                        </td>
                        <td>£{(fullPrice - discount).toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          <div className="edit-invoice-card">
            <h3>Add Line Item</h3>
            <div className="edit-invoice-catalog-search">
              <input
                type="text"
                placeholder="Search purchasable items..."
                value={catalogSearch}
                onChange={handleCatalogSearch}
              />
              {catalogResults.length > 0 && (
                <ul className="edit-invoice-catalog-results">
                  {catalogResults.map((item) => (
                    <li key={item.id} onClick={() => addCatalogItem(item)}>
                      {item.item_type} {item.item_name} - {item.item_description} - £{item.item_price}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {newItems.length > 0 && (
              <>
                <table className="edit-invoice-items-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Full Price</th>
                      <th>Discount</th>
                      <th>Price</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {newItems.map((item) => (
                      <tr key={item._key}>
                        <td>{item.product_type} {item.product_name}</td>
                        <td>£{Number(item.line_item_full_price).toFixed(2)}</td>
                        <td>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.line_item_discount}
                            onChange={(e) => updateNewItemDiscount(item._key, e.target.value)}
                          />
                        </td>
                        <td>£{(Number(item.line_item_full_price) - Number(item.line_item_discount || 0)).toFixed(2)}</td>
                        <td>
                          <Button type="button" variant="danger" onClick={() => removeNewItem(item._key)}>Remove</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="edit-invoice-newitems-total">New items total: £{newItemsTotal.toFixed(2)}</div>
              </>
            )}
          </div>
        </div>

        <div className="edit-invoice-preview-col">
          <div className="edit-invoice-preview-card">
            <div className="edit-invoice-preview-header">
              <h3>Live Preview</h3>
              {previewLoading && <span className="edit-invoice-preview-status">Updating...</span>}
            </div>
            {previewUrl ? (
              <iframe src={previewUrl} title="Invoice preview" className="edit-invoice-preview-frame" />
            ) : (
              <div className="edit-invoice-preview-placeholder">Generating preview...</div>
            )}
          </div>

          <div className="edit-invoice-preview-card">
            <Attachments entityType="invoice" entityId={invoiceNumber} />
          </div>
        </div>
      </div>
    </PageShell>
  );
}

export default EditInvoice;
